import { useEffect, useMemo, useRef, useState } from 'react';
import { DeleteButton, Empty, ErrorBanner, Field, Loading, Modal, PageHead } from '../components/ui';
import { api } from '../lib/api';
import { downloadCsv, fileStamp } from '../lib/download';
import { formatDate, humanise, toLocalDateString } from '../lib/format';
import { useResource } from '../lib/hooks';
import { NAV, type NavGroup } from '../components/Shell';
import { pageKey } from '../lib/pages';
import type {
  BusinessArea,
  Department,
  Employee,
  EmployeeAccess,
  Role,
  RoleCreated,
  SelfReviewSummaryRow,
} from '../lib/types';
import { SelfReviewDialog } from './people/SelfReview';

type Tab = 'people' | 'anniversaries' | 'departments' | 'roles';

const BLANK = {
  firstName: '',
  lastName: '',
  email: '',
  departmentId: '' as number | '',
  roleId: '' as number | '',
  isLeadership: false,
  managerEmployeeId: '' as number | '',
  employmentType: 'full_time',
  status: 'active',
  startDate: '',
};

/**
 * The directory.
 *
 * Granted to a manager as well as to an admin, and the two see different
 * pages. An admin sees everyone and administers them. Anybody else sees
 * themselves and the people under them — the API decides that, from
 * `scope=directory` below — and the page drops to reading: no Add, no Edit,
 * no Departments or Roles, because every one of those is requireAdmin on the
 * other end and a button that can only fail is worse than no button. The one
 * thing a manager can still DO here is open a review of somebody on their
 * team — and only if they have the People page, since that is whose endpoint
 * a review is. Hence `canReview`, decided in App.tsx.
 */
export function EmployeesPage({ isAdmin, canReview }: { isAdmin: boolean; canReview: boolean }) {
  const [tab, setTab] = useState<Tab>('people');
  const [search, setSearch] = useState('');
  const [showTerminated, setShowTerminated] = useState(false);

  /* `scope=directory` asks for the Employees page's own list rather than the
     app-wide people picker every other screen reads. Who is in it is settled
     on the server; this flag only says which of the two lists is wanted. */
  const employees = useResource<Employee[]>(
    `/employees?scope=directory&search=${encodeURIComponent(search)}${showTerminated ? '' : '&status=active'}`,
  );
  const departments = useResource<Department[]>('/departments');
  const roles = useResource<Role[]>('/roles');

  const [editing, setEditing] = useState<Employee | null>(null);
  const [creating, setCreating] = useState(false);
  /* A manager review, opened from the list or from somebody's Edit dialog.
     Opened from the dialog, the dialog steps aside while the review is up —
     two dialogs would both answer Escape — and comes back for the same person
     when the review closes. */
  const [reviewing, setReviewing] = useState<{
    employee: Employee;
    id: number | null;
    fromEdit: boolean;
  } | null>(null);
  const activeEmployees = useResource<Employee[]>('/employees?scope=directory&status=active');

  /* The People list as a spreadsheet — whoever it shows, so the search and
     the past-employees box decide who is in it. The start date is the one
     the Started column shows, or, for somebody without one, the hire date on
     their Autotask Resource — the Anniversaries tab's rule — with a column
     saying which it was. Autotask being unreachable leaves only the start
     dates, not a failed export. */
  const [exporting, setExporting] = useState(false);
  const exportList = async () => {
    const list = employees.data ?? [];
    setExporting(true);
    let hired: Record<number, string> = {};
    try {
      hired = await api.get<Record<number, string>>('/people/directory/hire-dates');
    } catch {
      /* Start dates only. */
    }
    setExporting(false);
    downloadCsv(`employees_${fileStamp()}.csv`, [
      ['Name', 'First name', 'Last name', 'Email', 'Department', 'Role', 'Reports to', 'Type', 'Status', 'Start date', 'Start date from', 'End date'],
      ...list.map((e) => {
        const own = e.StartDate?.slice(0, 10) || null;
        const start = own || hired[e.EmployeeId] || null;
        return [
          e.FullName,
          e.FirstName,
          e.LastName,
          e.Email,
          e.DepartmentName,
          e.RoleTitle,
          e.ManagerName,
          humanise(e.EmploymentType),
          humanise(e.Status),
          start,
          own ? 'Directory' : start ? 'Autotask' : '',
          e.EndDate?.slice(0, 10) || null,
        ];
      }),
    ]);
  };

  return (
    <>
      <PageHead
        title="Employees"
        lede={
          isAdmin
            ? 'The shared directory. Every other tool in the estate reads these people, their department and their role — so this is the one place to change them.'
            : 'You and the people who report to you — their department, their role, and when they started.'
        }
        actions={
          isAdmin ? (
            <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
              Add employee
            </button>
          ) : undefined
        }
      />

      <div className="tabs">
        <button className={`tab${tab === 'people' ? ' active' : ''}`} onClick={() => setTab('people')}>
          People
        </button>
        <button className={`tab${tab === 'anniversaries' ? ' active' : ''}`} onClick={() => setTab('anniversaries')}>
          Anniversaries
        </button>
        {/* The two configuration lists. Admin-only, like everything they do:
            the tabs are Add, Edit and Delete and nothing else. */}
        {isAdmin && (
          <>
            <button className={`tab${tab === 'departments' ? ' active' : ''}`} onClick={() => setTab('departments')}>
              Departments
            </button>
            <button className={`tab${tab === 'roles' ? ' active' : ''}`} onClick={() => setTab('roles')}>
              Roles
            </button>
          </>
        )}
      </div>

      {tab === 'people' && (
        <div className="stack">
          <div className="row wrap">
            <input
              className="input"
              style={{ maxWidth: 320 }}
              placeholder="Search by name or email"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <label className="checkbox">
              <input
                type="checkbox"
                checked={showTerminated}
                onChange={(e) => setShowTerminated(e.target.checked)}
              />
              Include past employees
            </label>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              style={{ marginLeft: 'auto' }}
              disabled={!employees.data?.length || exporting}
              onClick={exportList}
            >
              {exporting ? 'Exporting…' : 'Export to spreadsheet'}
            </button>
          </div>

          {employees.error && <ErrorBanner message={employees.error} />}
          {employees.loading && <Loading />}

          {employees.data && employees.data.length === 0 && (
            <Empty title={isAdmin ? 'No employees yet' : 'Nobody to show'}>
              <p>
                {isAdmin
                  ? 'Add your first person to start building the accountability chart.'
                  : 'Your own record is not here, and nobody reports to you. An administrator can check who you are linked to on the directory.'}
              </p>
            </Empty>
          )}

          {employees.data && employees.data.length > 0 && (
            <div className="card">
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Department</th>
                      <th>Role</th>
                      <th>Reports to</th>
                      <th>Type</th>
                      <th>Started</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {employees.data.map((e) => (
                      <tr key={e.EmployeeId}>
                        <td>
                          <div className="strong">{e.FullName}</div>
                          {e.Email && <div className="xs muted">{e.Email}</div>}
                        </td>
                        <td>{e.DepartmentName ?? <span className="subtle">—</span>}</td>
                        <td>
                          {e.RoleTitle ?? <span className="subtle">—</span>}
                          {e.IsLeadership && <span className="badge badge-good" style={{ marginLeft: 8 }}>Leadership</span>}
                        </td>
                        <td>{e.ManagerName ?? <span className="subtle">—</span>}</td>
                        <td>
                          <span className={`badge ${e.Status === 'active' ? 'badge-neutral' : 'badge-critical'}`}>
                            {e.Status === 'active' ? humanise(e.EmploymentType) : humanise(e.Status)}
                          </span>
                        </td>
                        <td>
                          {/* New review sits here, beside the start date, so a
                              review is one click from the list rather than two
                              through Edit. */}
                          <div className="row" style={{ flexWrap: 'nowrap' }}>
                            <span className="mono xs">{formatDate(e.StartDate)}</span>
                            {canReview && (
                              <button
                                type="button"
                                className="btn btn-primary btn-sm"
                                onClick={() => setReviewing({ employee: e, id: null, fromEdit: false })}
                              >
                                New review
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="actions">
                          {isAdmin && (
                            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditing(e)}>
                              Edit
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'anniversaries' && <AnniversaryCalendar onOpen={isAdmin ? setEditing : undefined} />}

      {tab === 'departments' && isAdmin && (
        <SimpleList
          kind="department"
          rows={departments.data ?? []}
          loading={departments.loading}
          error={departments.error}
          reload={() => {
            departments.reload();
            employees.reload();
          }}
        />
      )}

      {tab === 'roles' && isAdmin && (
        <RoleList
          roles={roles.data ?? []}
          departments={departments.data ?? []}
          loading={roles.loading}
          error={roles.error}
          reload={() => {
            roles.reload();
            employees.reload();
          }}
        />
      )}

      {(creating || editing) && (
        <EmployeeForm
          employee={editing}
          employees={employees.data ?? []}
          departments={departments.data ?? []}
          roles={roles.data ?? []}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onReview={(employee, id) => {
            setEditing(null);
            setReviewing({ employee, id, fromEdit: true });
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            employees.reload();
            departments.reload();
            roles.reload();
          }}
        />
      )}

      {reviewing && (
        <SelfReviewDialog
          kind="manager"
          employeeId={reviewing.employee.EmployeeId}
          employeeName={reviewing.employee.FullName}
          firstName={reviewing.employee.FirstName}
          selfReviewId={reviewing.id}
          defaults={{
            jobTitle: reviewing.employee.RoleTitle,
            reviewerEmployeeId: reviewing.employee.ManagerEmployeeId,
          }}
          employees={activeEmployees.data ?? []}
          onClose={() => {
            if (reviewing.fromEdit) setEditing(reviewing.employee);
            setReviewing(null);
          }}
          onSaved={() => {
            if (reviewing.fromEdit) setEditing(reviewing.employee);
            setReviewing(null);
          }}
        />
      )}
    </>
  );
}

function EmployeeForm({
  employee,
  employees,
  departments,
  roles,
  onClose,
  onReview,
  onSaved,
}: {
  employee: Employee | null;
  employees: Employee[];
  departments: Department[];
  roles: Role[];
  onClose: () => void;
  /** Opens a manager review of this person — `null` for a new one. */
  onReview: (employee: Employee, reviewId: number | null) => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState(
    employee
      ? {
          firstName: employee.FirstName,
          lastName: employee.LastName,
          email: employee.Email ?? '',
          departmentId: (employee.DepartmentId ?? '') as number | '',
          roleId: (employee.RoleId ?? '') as number | '',
          isLeadership: employee.IsLeadership ?? false,
          managerEmployeeId: (employee.ManagerEmployeeId ?? '') as number | '',
          employmentType: employee.EmploymentType as string,
          status: employee.Status as string,
          startDate: employee.StartDate?.slice(0, 10) ?? '',
        }
      : BLANK,
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /* The card list comes from the dashboard endpoint rather than a second copy
     of the same table. Only admins reach this form, and an admin is granted
     every area, so what comes back is the full list. */
  const areas = useResource<BusinessArea[]>('/dashboard/areas');
  const access = useResource<EmployeeAccess>(
    employee ? `/employees/${employee.EmployeeId}/access` : null,
  );

  const [accessLevel, setAccessLevel] = useState<'admin' | 'user'>('user');
  /* null until we know: for an existing person that means "still loading", and
     rendering every box unticked in the meantime would look like a decision
     somebody made.

     A NEW STARTER BEGINS WITH NOTHING — no dashboard cards and no pages. An
     admin ticks what the role needs; access is granted, never assumed. (Until
     now a new record started with every card and every non-leadership page
     ticked, which handed a new hire the whole app unless somebody remembered
     to untick it.) */
  const [areaIds, setAreaIds] = useState<number[] | null>(employee ? null : []);
  /* The pages they may open (migration 144). null until known, for the same
     reason as the cards: an empty list on screen would read as a decision. */
  const [pages, setPages] = useState<string[] | null>(employee ? null : []);

  useEffect(() => {
    if (access.data) {
      setAccessLevel(access.data.accessLevel);
      setAreaIds(access.data.areas.filter((a) => a.Granted).map((a) => a.AreaId));
      setPages(access.data.pages ?? []);
    }
  }, [access.data]);

  const toggleArea = (id: number) =>
    setAreaIds((current) =>
      (current ?? []).includes(id)
        ? (current ?? []).filter((x) => x !== id)
        : [...(current ?? []), id],
    );

  const togglePage = (key: string) =>
    setPages((current) =>
      (current ?? []).includes(key)
        ? (current ?? []).filter((k) => k !== key)
        : [...(current ?? []), key],
    );

  /* A section heading ticks or clears everything under it: all on if any is
     off, all off if every one is on. */
  const toggleSection = (group: NavGroup) => {
    const keys = assignableKeys(group);
    setPages((current) => {
      const now = current ?? [];
      const allOn = keys.every((k) => now.includes(k));
      return allOn ? now.filter((k) => !keys.includes(k)) : [...new Set([...now, ...keys])];
    });
  };

  /* Until the ticks are known, saving would post an empty set and revoke
     every card and page the person has. An admin needs no list at all, so
     they are ready immediately. */
  const accessReady = accessLevel === 'admin' || (areaIds !== null && pages !== null);

  const set = (patch: Partial<typeof BLANK>) => setForm((f) => ({ ...f, ...patch }));

  const submit = async () => {
    setSaving(true);
    setError(null);
    const payload = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim() || null,
      departmentId: form.departmentId === '' ? null : Number(form.departmentId),
      roleId: form.roleId === '' ? null : Number(form.roleId),
      isLeadership: form.isLeadership,
      managerEmployeeId: form.managerEmployeeId === '' ? null : Number(form.managerEmployeeId),
      employmentType: form.employmentType,
      status: form.status,
      startDate: form.startDate || null,
    };
    try {
      /* The employee first, because access hangs off an employee id that does
         not exist yet for a new starter. */
      const saved = employee
        ? await api.put<{ EmployeeId: number }>(`/employees/${employee.EmployeeId}`, payload)
        : await api.post<{ EmployeeId: number }>('/employees', payload);

      const id = employee?.EmployeeId ?? saved?.EmployeeId;
      if (id) {
        /* areaIds and pages are omitted rather than sent empty when the ticks
           were never loaded — an admin's form does not render them at all, and
           an empty array would revoke everything they had if they were later
           demoted. */
        await api.put(`/employees/${id}/access`, {
          accessLevel,
          ...(areaIds !== null && { areaIds }),
          ...(pages !== null && { pages }),
        });
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save');
      setSaving(false);
    }
  };

  /* A manager list that excludes the person being edited — the API rejects
     self-management anyway, but not offering it is kinder. */
  const managerOptions = employees.filter((e) => e.EmployeeId !== employee?.EmployeeId);

  return (
    <Modal
      title={employee ? `Edit ${employee.FullName}` : 'Add employee'}
      onClose={onClose}
      footer={
        <>
          {employee && (
            <DeleteButton
              what={employee.FullName}
              label="Mark as departed"
              onConfirm={() => {
                void api.del(`/employees/${employee.EmployeeId}`).then(onSaved);
              }}
            />
          )}
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={saving || !form.firstName.trim() || !form.lastName.trim() || !accessReady}
            onClick={() => void submit()}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}

      <div className="form-grid">
        <Field label="First name">
          <input className="input" value={form.firstName} onChange={(e) => set({ firstName: e.target.value })} />
        </Field>
        <Field label="Last name">
          <input className="input" value={form.lastName} onChange={(e) => set({ lastName: e.target.value })} />
        </Field>
      </div>

      <Field label="Email" hint="Used to link this person to their sign-in account.">
        <input className="input" type="email" value={form.email} onChange={(e) => set({ email: e.target.value })} />
      </Field>

      <div className="form-grid">
        <Field label="Department">
          <select
            className="select"
            value={form.departmentId}
            onChange={(e) => set({ departmentId: e.target.value === '' ? '' : Number(e.target.value) })}
          >
            <option value="">Unassigned</option>
            {departments.map((d) => (
              <option key={d.DepartmentId} value={d.DepartmentId}>
                {d.Name}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Role">
          <select
            className="select"
            value={form.roleId}
            onChange={(e) => set({ roleId: e.target.value === '' ? '' : Number(e.target.value) })}
          >
            <option value="">Unassigned</option>
            {roles.map((r) => (
              <option key={r.RoleId} value={r.RoleId}>
                {r.Title}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <div className="form-grid">
        <Field
          label="Leadership team"
          hint="Marks them as part of the leadership team. It no longer opens pages on its own — tick those under Pages below. Their role's own leadership flag is separate."
        >
          <label className="checkbox">
            <input
              type="checkbox"
              checked={form.isLeadership}
              onChange={(e) => set({ isLeadership: e.target.checked })}
            />
            On the leadership team
          </label>
        </Field>

        <Field label="Reports to">
          <select
            className="select"
            value={form.managerEmployeeId}
            onChange={(e) => set({ managerEmployeeId: e.target.value === '' ? '' : Number(e.target.value) })}
          >
            <option value="">Nobody</option>
            {managerOptions.map((e) => (
              <option key={e.EmployeeId} value={e.EmployeeId}>
                {e.FullName}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Employment type">
          <select className="select" value={form.employmentType} onChange={(e) => set({ employmentType: e.target.value })}>
            <option value="full_time">Full time</option>
            <option value="part_time">Part time</option>
            <option value="contractor">Contractor</option>
            <option value="intern">Intern</option>
          </select>
        </Field>
      </div>

      <div className="form-grid">
        <Field label="Status">
          <select className="select" value={form.status} onChange={(e) => set({ status: e.target.value })}>
            <option value="active">Active</option>
            <option value="on_leave">On leave</option>
            <option value="terminated">Departed</option>
          </select>
        </Field>

        <Field label="Start date">
          <input className="input" type="date" value={form.startDate} onChange={(e) => set({ startDate: e.target.value })} />
        </Field>
      </div>

      {employee && <EmployeeReviews employee={employee} onReview={(id) => onReview(employee, id)} />}

      {/* --- Access ---------------------------------------------------------
          Set here rather than on a separate screen because it is part of
          deciding who somebody is. The API enforces all of it; this is the
          form, not the control. */}
      <div className="access-block">
        <div className="eyebrow">Access</div>

        <Field
          label="Level"
          hint={
            accessLevel === 'admin'
              ? 'Full control, including the employee directory and everyone’s access.'
              : 'The pages ticked below, and the dashboard cards ticked below.'
          }
        >
          <select
            className="select"
            value={accessLevel}
            onChange={(e) => setAccessLevel(e.target.value as 'admin' | 'user')}
          >
            <option value="user">User</option>
            <option value="admin">Admin</option>
          </select>
        </Field>

        <div className="field">
          <label>Dashboard cards</label>
          <p className="xs muted">
            Which tiles appear on their dashboard. What they can open is set under Pages below — a
            tile for a page they do not have shows its figures but does not link through.
          </p>

          {accessLevel === 'admin' ? (
            <p className="small muted mt-2">
              An admin sees every card. Set the level to User to choose individual cards.
            </p>
          ) : areas.loading || areaIds === null ? (
            <Loading label="Loading cards" />
          ) : (
            <div className="access-cards mt-2">
              {(areas.data ?? []).map((a) => (
                <label key={a.AreaId} className="checkbox">
                  <input
                    type="checkbox"
                    checked={areaIds.includes(a.AreaId)}
                    onChange={() => toggleArea(a.AreaId)}
                  />
                  {a.Name}
                </label>
              ))}
            </div>
          )}

          {accessLevel !== 'admin' && areaIds !== null && areaIds.length === 0 && (
            <p className="xs mt-2" style={{ color: 'var(--text-accent)' }}>
              With no cards ticked they will sign in to an empty dashboard.
            </p>
          )}
        </div>

        <div className="field">
          <label>Pages</label>
          <p className="xs muted">
            Every page in the menu, grouped as it is there. Tick a section to give them everything
            in it, or tick pages one by one. An unticked page leaves their menu and will not open.
          </p>

          {accessLevel === 'admin' ? (
            <p className="small muted mt-2">
              An admin sees every page. Set the level to User to choose individual pages.
            </p>
          ) : pages === null ? (
            <Loading label="Loading pages" />
          ) : (
            <div className="access-tree mt-2">
              {NAV.map((group) => {
                const keys = assignableKeys(group);
                const on = keys.filter((k) => pages.includes(k)).length;
                return (
                  <div key={group.section} className="access-tree-group">
                    <label className={`checkbox access-tree-head${keys.length === 0 ? ' is-locked' : ''}`}>
                      <SectionBox
                        checked={keys.length > 0 && on === keys.length}
                        partial={on > 0 && on < keys.length}
                        disabled={keys.length === 0}
                        onChange={() => toggleSection(group)}
                      />
                      <strong>{group.section}</strong>
                      {keys.length === 0 ? (
                        <span className="xs subtle">admin only</span>
                      ) : (
                        <span className="xs subtle">
                          {on} of {keys.length}
                        </span>
                      )}
                    </label>
                    <div className="access-tree-items">
                      {group.items.map((item) => {
                        const key = pageKey(item.to);
                        const locked = Boolean(group.adminOnly || item.adminOnly);
                        return (
                          <label key={key} className={`checkbox${locked ? ' is-locked' : ''}`}>
                            <input
                              type="checkbox"
                              checked={!locked && pages.includes(key)}
                              disabled={locked}
                              onChange={() => togglePage(key)}
                            />
                            <span>
                              {item.label}
                              {locked && !group.adminOnly && <span className="xs subtle"> · admin only</span>}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {accessLevel !== 'admin' && pages !== null && pages.length === 0 && (
            <p className="xs mt-2" style={{ color: 'var(--text-accent)' }}>
              With no pages ticked they can sign in but open nothing.
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
}

/* --- Anniversaries ----------------------------------------------------------
   Work anniversaries as a month, from core.Employee.StartDate — the date the
   People column shows as "Started" — or, for somebody with none, the hire
   date on their Autotask Resource. Current staff only, whatever the People
   tab's search or past-employees box is set to: an anniversary belongs to
   somebody still here. The first year is not one, so a person appears from
   their first anniversary on. A 29 February start falls on the 28th in other
   years. Every fifth year is marked as a milestone. Uses the review-schedule
   calendar's grid (the am-cal styles).
   -------------------------------------------------------------------------- */

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const isLeap = (y: number) => (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;

/** `onOpen` is absent for anybody who cannot edit a record: the day still
 *  names the person and the years, it just does not open a form that would
 *  be refused. The list is scoped the same way the People tab is. */
function AnniversaryCalendar({ onOpen }: { onOpen?: (e: Employee) => void }) {
  const people = useResource<Employee[]>('/employees?scope=directory&status=active');
  /* A failed Autotask read leaves only the start dates — not an error. */
  const hired = useResource<Record<number, string>>('/people/directory/hire-dates');
  const today = toLocalDateString(new Date());
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  const dateOf = (e: Employee) => e.StartDate?.slice(0, 10) || hired.data?.[e.EmployeeId] || null;

  /* Keyed on "MM-DD", with the start year kept to count the years. */
  const byDay = useMemo(() => {
    const map = new Map<string, { e: Employee; year: number; started: string; fromAutotask: boolean }[]>();
    for (const e of people.data ?? []) {
      const iso = e.StartDate?.slice(0, 10) || hired.data?.[e.EmployeeId];
      if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) continue;
      const key = iso.slice(5);
      const list = map.get(key) ?? [];
      list.push({ e, year: Number(iso.slice(0, 4)), started: iso, fromAutotask: !e.StartDate });
      map.set(key, list);
    }
    for (const list of map.values()) list.sort((a, b) => a.e.FullName.localeCompare(b.e.FullName));
    return map;
  }, [people.data, hired.data]);

  /* Whole weeks, Sunday first, covering the month. */
  const days = useMemo(() => {
    const y = month.getFullYear();
    const m = month.getMonth();
    const lead = month.getDay();
    const count = Math.ceil((lead + new Date(y, m + 1, 0).getDate()) / 7) * 7;
    return Array.from({ length: count }, (_, i) => new Date(y, m, 1 - lead + i));
  }, [month]);

  const onDay = (d: Date) => {
    const key = toLocalDateString(d).slice(5);
    const list = [...(byDay.get(key) ?? [])];
    if (key === '02-28' && !isLeap(d.getFullYear())) list.push(...(byDay.get('02-29') ?? []));
    return list
      .map((x) => ({ ...x, years: d.getFullYear() - x.year }))
      .filter((x) => x.years >= 1);
  };

  const inMonth = days
    .filter((d) => d.getMonth() === month.getMonth())
    .reduce((n, d) => n + onDay(d).length, 0);
  const undated = (people.data ?? []).filter((e) => !dateOf(e)).length;
  const fromAutotask = (people.data ?? []).filter((e) => !e.StartDate && dateOf(e)).length;

  const shift = (by: number) => setMonth(new Date(month.getFullYear(), month.getMonth() + by, 1));

  if (people.error) return <ErrorBanner message={people.error} />;
  if (people.loading && !people.data) return <Loading />;

  return (
    <div className="card">
      <div className="am-cal-bar">
        <div className="row">
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => shift(-1)} aria-label="Previous month">
            ‹
          </button>
          <h3 className="am-cal-title">
            {month.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
          </h3>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => shift(1)} aria-label="Next month">
            ›
          </button>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => {
              const d = new Date();
              setMonth(new Date(d.getFullYear(), d.getMonth(), 1));
            }}
          >
            Today
          </button>
        </div>
        <span className="xs muted">
          {inMonth} anniversar{inMonth === 1 ? 'y' : 'ies'} this month
        </span>
      </div>

      <div className="am-cal-wrap">
        <div className="am-cal" role="grid" aria-label="Work anniversaries">
          {WEEKDAYS.map((d) => (
            <div key={d} className="am-cal-dow" role="columnheader">
              {d}
            </div>
          ))}
          {days.map((d) => {
            const key = toLocalDateString(d);
            const outside = d.getMonth() !== month.getMonth();
            return (
              <div
                key={key}
                role="gridcell"
                className={`am-cal-day${outside ? ' is-outside' : ''}${key === today ? ' is-today' : ''}`}
              >
                <div className="am-cal-date">{d.getDate()}</div>
                {onDay(d).map(({ e, years, started, fromAutotask }) => {
                  const title = `${e.FullName} · ${years} year${years === 1 ? '' : 's'} · ${fromAutotask ? 'hired' : 'started'} ${formatDate(started)}${fromAutotask ? ' (Autotask)' : ''}`;
                  const className = `am-cal-item${years % 5 === 0 ? ' is-milestone' : ''}`;
                  const inner = (
                    <>
                      <span className="am-cal-client">{e.FullName}</span>
                      <span className="am-cal-manager">
                        {years % 5 === 0 && '★ '}
                        {years} year{years === 1 ? '' : 's'}
                      </span>
                    </>
                  );
                  return onOpen ? (
                    <button
                      key={e.EmployeeId}
                      type="button"
                      className={className}
                      onClick={() => onOpen(e)}
                      title={title}
                    >
                      {inner}
                    </button>
                  ) : (
                    <div key={e.EmployeeId} className={className} title={title}>
                      {inner}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

      {(fromAutotask > 0 || undated > 0 || hired.loading) && (
        <p className="xs muted am-cal-foot">
          {hired.loading
            ? 'Reading hire dates from Autotask…'
            : fromAutotask > 0 &&
              `${fromAutotask} ${fromAutotask === 1 ? 'date comes' : 'dates come'} from Autotask, for people with no start date here. `}
          {!hired.loading &&
            undated > 0 &&
            `${undated} ${undated === 1 ? 'person has' : 'people have'} no start date${hired.error ? '' : ' here or in Autotask'} and can't be placed — add one on the People tab.`}
          {hired.error && ` Autotask could not be read: ${hired.error}`}
        </p>
      )}
    </div>
  );
}

/* --- Page access helpers (migration 144) ------------------------------------ */

/** The pages in a menu section that can be granted — not the admin-only ones. */
/**
 * Hire Date, and this person's manager reviews — on the Manager Assessment
 * Form (migration 173).
 *
 * A review written here is PRIVATE until it is shared: it stays on this page,
 * which only admins can open, and only a shared one appears on the person's
 * People record. Hire Date is Autotask's, read live, and is not editable here
 * any more than it is on People.
 */
function EmployeeReviews({ employee, onReview }: { employee: Employee; onReview: (reviewId: number | null) => void }) {
  const hire = useResource<{ found: boolean; hireDate: string | null }>(
    `/people/directory/${employee.EmployeeId}/hire-date`,
  );
  const reviews = useResource<SelfReviewSummaryRow[]>(
    `/people/self-reviews/employee/${employee.EmployeeId}?kind=manager`,
  );
  const list = reviews.data ?? [];

  return (
    <div className="access-block">
      <div className="eyebrow">Reviews</div>
      <div className="field">
        <label>Hire Date</label>
        <div className="row">
          <p className="small">{hire.loading ? '…' : formatDate(hire.data?.hireDate)}</p>
          <button type="button" className="btn btn-primary btn-sm" onClick={() => onReview(null)}>
            New review
          </button>
        </div>
        <p className="xs subtle">From Autotask; changed there.</p>
      </div>

      {reviews.error && <ErrorBanner message={reviews.error} />}
      {list.length > 0 && (
        <div className="field">
          <label>Reviews of {employee.FirstName}</label>
          {list.map((r) => (
            <div key={r.SelfReviewId} className="row-between mini-row">
              <span className="small">
                <span className="strong">
                  {r.PeriodFrom || r.PeriodTo
                    ? `${formatDate(r.PeriodFrom)} – ${formatDate(r.PeriodTo)}`
                    : `Started ${formatDate(r.CreatedUtc)}`}
                </span>
                {' · '}
                {r.RatedCount} / {r.ItemCount} rated
              </span>
              <span className="row">
                <span className={`badge ${r.SharedUtc ? 'badge-good' : 'badge-warning'}`}>
                  {r.SharedUtc ? `Shared ${formatDate(r.SharedUtc)}` : 'Private'}
                </span>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => onReview(r.SelfReviewId)}>
                  Open
                </button>
                <DeleteButton
                  what="this review"
                  onConfirm={() => {
                    void api.del(`/people/self-reviews/${r.SelfReviewId}`).then(() => reviews.reload());
                  }}
                />
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function assignableKeys(group: NavGroup): string[] {
  if (group.adminOnly) return [];
  return group.items.filter((i) => !i.adminOnly).map((i) => pageKey(i.to));
}

/** A section's tick box, which shows a dash when only some pages under it
 *  are ticked. `indeterminate` is a property, not an attribute, so it is set
 *  on the element directly. */
function SectionBox({
  checked,
  partial,
  disabled,
  onChange,
}: {
  checked: boolean;
  partial: boolean;
  disabled: boolean;
  onChange: () => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = partial;
  }, [partial]);
  return <input ref={ref} type="checkbox" checked={checked} disabled={disabled} onChange={onChange} />;
}

/* --- Departments --------------------------------------------------------- */

function SimpleList({
  kind,
  rows,
  loading,
  error,
  reload,
}: {
  kind: 'department';
  rows: Department[];
  loading: boolean;
  error: string | null;
  reload: () => void;
}) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const add = async () => {
    if (!name.trim()) return;
    setBusy(true);
    setProblem(null);
    try {
      await api.post('/departments', { name: name.trim(), sortOrder: rows.length * 10 });
      setName('');
      reload();
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Could not add');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: number) => {
    setProblem(null);
    try {
      await api.del(`/departments/${id}`);
      reload();
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Could not delete');
    }
  };

  return (
    <div className="stack">
      {error && <ErrorBanner message={error} />}
      {problem && <ErrorBanner message={problem} />}
      {loading && <Loading />}

      <div className="card">
        <div className="card-body">
          <div className="row">
            <input
              className="input"
              placeholder={`New ${kind} name`}
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void add();
              }}
            />
            <button type="button" className="btn btn-primary" disabled={busy || !name.trim()} onClick={() => void add()}>
              Add
            </button>
          </div>
        </div>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Department</th>
                <th className="num">People</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.DepartmentId}>
                  <td className="strong">{d.Name}</td>
                  <td className="num">{d.EmployeeCount ?? 0}</td>
                  <td className="actions">
                    <DeleteButton what={d.Name} onConfirm={() => void remove(d.DepartmentId)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* --- Roles ---------------------------------------------------------------- */

function RoleList({
  roles,
  departments,
  loading,
  error,
  reload,
}: {
  roles: Role[];
  departments: Department[];
  loading: boolean;
  error: string | null;
  reload: () => void;
}) {
  const [title, setTitle] = useState('');
  const [departmentId, setDepartmentId] = useState<number | ''>('');
  const [isLeadership, setIsLeadership] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [provisioned, setProvisioned] = useState<string | null>(null);
  const [editing, setEditing] = useState<Role | null>(null);

  const add = async () => {
    if (!title.trim()) return;
    setProblem(null);
    setProvisioned(null);
    try {
      const created = await api.post<RoleCreated>('/roles', {
        title: title.trim(),
        departmentId: departmentId === '' ? null : Number(departmentId),
        isLeadership,
      });
      /* Adding a role now also creates a People Template, a training-matrix
         row and a set of interview questions. Said out loud, because a side
         effect on three other screens that nobody was told about is
         indistinguishable from a bug the first time somebody finds it. */
      const pack = created?.Provisioned;
      if (pack) {
        const q = pack.RecruitingQuestions;
        setProvisioned(
          `Added ${created.Title}, with a People Template, a row on the training matrix and ` +
            `${q} interview ${q === 1 ? 'question' : 'questions'}` +
            `${pack.SampleMatched ? ` from the ${pack.SampleLabel} set` : ' from the general set'}. ` +
            `Write the job description on the People Template, or upload the document on Job Descriptions.`,
        );
      }
      setTitle('');
      setIsLeadership(false);
      reload();
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Could not add');
    }
  };

  const remove = async (id: number) => {
    setProblem(null);
    try {
      await api.del(`/roles/${id}`);
      reload();
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Could not delete');
    }
  };

  return (
    <div className="stack">
      {error && <ErrorBanner message={error} />}
      {problem && <ErrorBanner message={problem} />}
      {provisioned && (
        <div className="alert alert-info">
          {provisioned}
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => setProvisioned(null)}
            aria-label="Dismiss"
          >
            Dismiss
          </button>
        </div>
      )}
      {loading && <Loading />}

      <div className="card">
        <div className="card-body">
          <div className="row wrap">
            <input
              className="input"
              style={{ maxWidth: 280 }}
              placeholder="New role title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <select
              className="select"
              style={{ maxWidth: 220 }}
              value={departmentId}
              onChange={(e) => setDepartmentId(e.target.value === '' ? '' : Number(e.target.value))}
            >
              <option value="">No department</option>
              {departments.map((d) => (
                <option key={d.DepartmentId} value={d.DepartmentId}>
                  {d.Name}
                </option>
              ))}
            </select>
            <label className="checkbox">
              <input type="checkbox" checked={isLeadership} onChange={(e) => setIsLeadership(e.target.checked)} />
              Leadership
            </label>
            <button type="button" className="btn btn-primary" disabled={!title.trim()} onClick={() => void add()}>
              Add
            </button>
          </div>
          <p className="xs muted mt-2">
            Roles marked as leadership appear on the GWC assessment board by default.
          </p>
        </div>

        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Role</th>
                <th>Department</th>
                <th className="num">People</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {roles.map((r) => (
                <tr key={r.RoleId}>
                  <td>
                    <span className="strong">{r.Title}</span>
                    {r.IsLeadership && <span className="badge badge-good" style={{ marginLeft: 8 }}>Leadership</span>}
                  </td>
                  <td>{r.DepartmentName ?? <span className="subtle">—</span>}</td>
                  <td className="num">{r.EmployeeCount ?? 0}</td>
                  <td className="actions">
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditing(r)}>
                      Edit
                    </button>
                    <DeleteButton what={r.Title} onConfirm={() => void remove(r.RoleId)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <RoleForm
          role={editing}
          departments={departments}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      )}
    </div>
  );
}

function RoleForm({
  role,
  departments,
  onClose,
  onSaved,
}: {
  role: Role;
  departments: Department[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    title: role.Title,
    description: role.Description ?? '',
    departmentId: (role.DepartmentId ?? '') as number | '',
    isLeadership: role.IsLeadership,
    isActive: role.IsActive,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.put(`/roles/${role.RoleId}`, {
        title: form.title.trim(),
        description: form.description.trim() || null,
        departmentId: form.departmentId === '' ? null : Number(form.departmentId),
        isLeadership: form.isLeadership,
        isActive: form.isActive,
      });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save');
      setSaving(false);
    }
  };

  return (
    <Modal
      title={`Edit ${role.Title}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={saving || !form.title.trim()}
            onClick={() => void submit()}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}

      <Field label="Title">
        <input className="input" value={form.title} onChange={(e) => set({ title: e.target.value })} />
      </Field>

      <Field label="Description" hint="Optional. What this seat is accountable for.">
        <textarea
          className="input"
          rows={3}
          value={form.description}
          onChange={(e) => set({ description: e.target.value })}
        />
      </Field>

      <div className="form-grid">
        <Field label="Department">
          <select
            className="select"
            value={form.departmentId}
            onChange={(e) => set({ departmentId: e.target.value === '' ? '' : Number(e.target.value) })}
          >
            <option value="">No department</option>
            {departments.map((d) => (
              <option key={d.DepartmentId} value={d.DepartmentId}>
                {d.Name}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Leadership" hint="Leadership roles appear on the GWC assessment board by default.">
          <label className="checkbox">
            <input
              type="checkbox"
              checked={form.isLeadership}
              onChange={(e) => set({ isLeadership: e.target.checked })}
            />
            Leadership role
          </label>
        </Field>

        <Field label="Active" hint="An inactive role stays on existing people but is retired from use.">
          <label className="checkbox">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => set({ isActive: e.target.checked })}
            />
            Active
          </label>
        </Field>
      </div>
    </Modal>
  );
}
